'use client';

import * as m from 'motion/react-m';

interface SkeletonLineProps {
  index: number;
  sectionIndex: number;
  widthPercent: number;
  /** Whether the line should be visible (false during building phase) */
  visible?: boolean;
  /** Additional delay offset for scroll-triggered animations (below-fold sections) */
  delayOffset?: number;
  /** Whether generation is complete - triggers 100% fill + glow animation */
  isComplete?: boolean;
}

/**
 * Calculate line fill opacity based on animation timing.
 * Earlier lines are lighter (60%), later lines are darker (80%).
 * Creates a visual progression where "fresh" content is lighter and "settled" content is darker.
 */
function getLineOpacity(totalDelay: number): number {
  const minOpacity = 60;
  const maxOpacity = 80;
  const maxDelay = 8; // Max expected delay in seconds
  // Clamp delay to maxDelay range and map to opacity
  const clampedDelay = Math.min(totalDelay, maxDelay);
  return Math.round(minOpacity + (clampedDelay / maxDelay) * (maxOpacity - minOpacity));
}

export function SkeletonLine({
  index,
  sectionIndex,
  widthPercent,
  visible = true,
  delayOffset = 0,
  isComplete = false,
}: SkeletonLineProps) {
  // Slower stagger rhythm for type-in animation (more visible progression)
  // For below-fold sections (sectionIndex >= 4), use shorter delays since they animate as they scroll in
  const isInitialSection = sectionIndex < 4;
  const baseDelay = isInitialSection ? sectionIndex * 0.4 + index * 0.2 : index * 0.15; // Slower stagger for initial sections
  const totalDelay = baseDelay + delayOffset;

  // Calculate progressive opacity based on timing (60% → 80%)
  // When complete, use 100% opacity
  const fillOpacity = isComplete ? 100 : getLineOpacity(totalDelay);

  // Target width: 100% when complete, otherwise the specified width
  const targetWidth = isComplete ? 100 : widthPercent;

  return (
    <m.div
      // Fade in when visible becomes true
      initial={{ opacity: 0 }}
      animate={{ opacity: visible ? 1 : 0 }}
      transition={{
        duration: 0.5, // More visible fade
        delay: visible ? totalDelay : 0,
        ease: 'easeOut',
      }}
      className="relative h-1.5 overflow-hidden rounded-md"
    >
      {/* Typing fill animation - grows from left to right when visible */}
      {/* When complete: fills to 100% with full opacity and subtle glow */}
      <m.div
        initial={{ width: '0%' }}
        animate={{
          width: visible ? `${targetWidth}%` : '0%',
          boxShadow: isComplete ? '0 0 8px hsl(var(--border) / 0.4)' : 'none',
        }}
        transition={{
          width: {
            duration: isComplete ? 0.2 : 1.0, // Fast fill on complete, slow typing otherwise
            delay: visible && !isComplete ? totalDelay + 0.1 : 0,
            ease: [0.23, 1, 0.32, 1], // ease-out-quint
          },
          boxShadow: {
            duration: 0.2,
            ease: 'easeOut',
          },
        }}
        className="absolute inset-y-0 left-0 rounded-md bg-background-secondary"
        style={{ opacity: fillOpacity / 100 }}
      />

      {/* Shimmer overlay - hidden when complete */}
      {visible && !isComplete && (
        <m.div
          initial={{ opacity: 0, x: '-100%' }}
          animate={{
            opacity: [0, 0.35, 0],
            x: ['-100%', '200%'],
          }}
          transition={{
            duration: 2.5, // Gentler shimmer pulse
            delay: totalDelay + 0.8,
            repeat: Infinity,
            repeatDelay: 2.0, // More pause between shimmers
            ease: 'easeInOut',
          }}
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent"
        />
      )}
    </m.div>
  );
}
